import type {GrupoDeExportacion, ResultadoDeCertificadosDeExportacion} from '@cnc/contracts';
import {useState} from 'react';
import {useCrearCertificadosDeExportacion, usePropuestaDeExportacion} from '../api/certificados';
import {ErrorDeApi} from '../api/cliente';
import {tr} from '../i18n/idioma';
import {numero} from '../utilidades/formato';

const campo = 'mt-1 rounded-md border border-linea px-2 py-1 text-sm text-tinta';
const boton = 'rounded-md border border-linea px-3 py-1.5 text-sm hover:bg-fondo disabled:opacity-50';

type Fila = {numero: string; analisis: string; fecha: string; ligar: boolean; incluir: boolean};
const llave = (g: GrupoDeExportacion) => `${g.recoleccion}|${g.clave}`;

// Todos los certificados que le faltan a una exportacion de una vez (decision
// 0088): los grupos de todas sus recolecciones con los consecutivos seguidos y
// una sola fecha de comercializacion para los C.O. Las fechas de los analisis
// van las sugeridas (se cambian, si hace falta, en la recoleccion).
export function CertificadosDeUnaExportacion() {
  const [texto, setTexto] = useState('');
  const [codigo, setCodigo] = useState<string | null>(null);
  const propuesta = usePropuestaDeExportacion(codigo);
  const crear = useCrearCertificadosDeExportacion();
  const [filas, setFilas] = useState<Record<string, Fila>>({});
  const [comercializado, setComercializado] = useState('');
  const [resultado, setResultado] = useState<ResultadoDeCertificadosDeExportacion | null>(null);
  const [error, setError] = useState<string | null>(null);
  const p = propuesta.data;

  const fila = (g: GrupoDeExportacion): Fila =>
    filas[llave(g)] ?? {
      numero: g.numeroSugerido === null ? '' : String(g.numeroSugerido),
      analisis: g.analisisSugerido ?? '',
      fecha: g.mesDeMolienda ?? '',
      ligar: g.coincide !== null,
      incluir: true,
    };
  const cambiar = (g: GrupoDeExportacion, cambio: Partial<Fila>) =>
    setFilas(antes => ({...antes, [llave(g)]: {...fila(g), ...cambio}}));
  const elegidos = p ? p.grupos.filter(g => fila(g).incluir) : [];
  const conOrigen = elegidos.filter(g => !g.soloAnalisis && !(g.coincide && fila(g).ligar));
  const incompleto =
    conOrigen.some(g => !fila(g).numero || !fila(g).fecha) || (conOrigen.length > 0 && !comercializado);

  const confirmar = () => {
    if (!p) return;
    setError(null);
    crear
      .mutateAsync({
        exportacion: p.codigo,
        peticion: {
          comercializado: comercializado || null,
          grupos: elegidos.map(g => {
            const f = fila(g);
            return {
              recoleccion: g.recoleccion,
              clave: g.clave,
              ligar: g.coincide !== null && f.ligar,
              numero: g.soloAnalisis ? null : Number(f.numero) || null,
              analisis: f.analisis.trim() || null,
              fecha: g.soloAnalisis ? null : f.fecha || null,
              fechasDelAnalisis: g.fechasSugeridas,
            };
          }),
        },
      })
      .then(
        r => {
          setResultado(r);
          setFilas({});
        },
        (err: unknown) =>
          setError(err instanceof ErrorDeApi ? err.message : tr('No se pudieron crear.', 'Could not create them.')),
      );
  };

  return (
    <div className="mt-6 rounded-lg border border-linea bg-white p-4 text-sm" data-prueba="certificados-de-exportacion">
      <h2 className="text-lg font-medium">
        {tr('Crear todos los certificados de una exportación', 'Create all the certificates of an export')}
      </h2>
      <p className="text-xs text-apagado">
        {tr(
          'Trae todas sus recolecciones con lo que les falta, con los números seguidos (C.O. por título y análisis por serie). Revisa, escribe una sola fecha de comercialización y crea todo de una vez: si algo falla, no se crea ninguno.',
          'Brings all its pickups with what they are missing, with consecutive numbers (C.O. by title and analysis by series). Review, type a single commercialization date and create everything at once: if something fails, none is created.',
        )}
      </p>
      <form
        className="mt-2 flex flex-wrap items-end gap-3"
        onSubmit={e => {
          e.preventDefault();
          setResultado(null);
          setFilas({});
          setCodigo(texto.trim() || null);
        }}
      >
        <label className="text-xs text-apagado">
          {tr('Exportación', 'Export')}
          <input
            value={texto}
            onChange={e => setTexto(e.target.value)}
            placeholder="MM102026.109"
            className={`${campo} block`}
            aria-label={tr('Exportación', 'Export')}
          />
        </label>
        <button type="submit" className={boton} disabled={!texto.trim() || propuesta.isFetching}>
          {propuesta.isFetching ? tr('Buscando…', 'Searching…') : tr('Ver lo que falta', 'See what is missing')}
        </button>
      </form>
      {propuesta.isError && <p className="mt-2 text-alerta">{propuesta.error.message}</p>}
      {p && (
        <div className="mt-3 space-y-3">
          <p className="text-xs text-apagado">
            {tr(
              `${p.codigo}: ${p.recolecciones.length} recolecciones (${p.recolecciones.join(', ')}); ${p.grupos.length} por crear y ${p.completos} que ya tienen sus certificados.`,
              `${p.codigo}: ${p.recolecciones.length} pickups (${p.recolecciones.join(', ')}); ${p.grupos.length} to create and ${p.completos} that already have their certificates.`,
            )}
          </p>
          {p.avisos.length > 0 && (
            <ul className="list-disc pl-5 text-xs text-apagado">
              {p.avisos.map(a => (
                <li key={a}>{a}</li>
              ))}
            </ul>
          )}
          {p.grupos.length > 0 && (
            <>
              <label className="block text-xs text-apagado">
                {tr('Fecha de comercialización (para todos los C.O.)', 'Commercialization date (for all the C.O.)')}
                <input
                  type="date"
                  value={comercializado}
                  onChange={e => setComercializado(e.target.value)}
                  className={`${campo} block`}
                />
              </label>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs text-apagado">
                    <tr>
                      <th className="pr-2" />
                      <th className="pr-2">{tr('Recolección', 'Pickup')}</th>
                      <th className="pr-2">{tr('Certificado', 'Certificate')}</th>
                      <th className="pr-2 text-right">BB</th>
                      <th className="pr-2 text-right">Au g</th>
                      <th className="pr-2">{tr('C.O. N°', 'C.O. No.')}</th>
                      <th className="pr-2">{tr('Análisis', 'Analysis')}</th>
                      <th className="pr-2">{tr('Fecha del C.O.', 'C.O. date')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {p.grupos.map(g => {
                      const f = fila(g);
                      const ligando = g.coincide !== null && f.ligar;
                      const nombre = g.soloAnalisis
                        ? `${g.exportador ?? ''} · ${tr('solo análisis', 'analysis only')}`
                        : `${g.titulo ?? ''}${g.planta ? ` · ${g.planta}` : ''}${g.sublote ? ` (${g.recoleccion}${g.sublote})` : ''}`;
                      return (
                        <tr key={llave(g)} className="border-t border-linea/60 align-top">
                          <td className="py-1 pr-2">
                            <input
                              type="checkbox"
                              checked={f.incluir}
                              onChange={e => cambiar(g, {incluir: e.target.checked})}
                              aria-label={tr(`Crear ${nombre}`, `Create ${nombre}`)}
                            />
                          </td>
                          <td className="py-1 pr-2">{g.recoleccion}</td>
                          <td className="py-1 pr-2">
                            {nombre}
                            {g.coincide && (
                              <label className="mt-1 flex items-center gap-1 text-xs text-apagado">
                                <input
                                  type="checkbox"
                                  checked={f.ligar}
                                  onChange={e => cambiar(g, {ligar: e.target.checked})}
                                />
                                {tr(
                                  `Ligar al C.O. ${g.coincide.numero} que ya existe (mismo oro)`,
                                  `Link to the existing C.O. ${g.coincide.numero} (same gold)`,
                                )}
                              </label>
                            )}
                          </td>
                          <td className="py-1 pr-2 text-right tabular-nums">{numero(g.bigBags)}</td>
                          <td className="py-1 pr-2 text-right tabular-nums">{numero(g.auG, 2)}</td>
                          <td className="py-1 pr-2">
                            {!g.soloAnalisis && !ligando && (
                              <input
                                value={f.numero}
                                onChange={e => cambiar(g, {numero: e.target.value.replace(/\D/g, '')})}
                                className={`${campo} w-20`}
                                aria-label={tr('Número del C.O.', 'C.O. number')}
                              />
                            )}
                          </td>
                          <td className="py-1 pr-2">
                            {!ligando && (
                              <input
                                value={f.analisis}
                                onChange={e => cambiar(g, {analisis: e.target.value})}
                                className={`${campo} w-24`}
                                aria-label={tr('Número del análisis', 'Analysis number')}
                              />
                            )}
                          </td>
                          <td className="py-1 pr-2">
                            {!g.soloAnalisis && !ligando && (
                              <input
                                type="date"
                                value={f.fecha}
                                onChange={e => cambiar(g, {fecha: e.target.value})}
                                className={campo}
                                aria-label={tr('Fecha del C.O.', 'C.O. date')}
                              />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={confirmar}
                  disabled={!elegidos.length || incompleto || crear.isPending}
                  className="rounded-md bg-verde px-4 py-2 text-sm font-medium text-white hover:bg-verde-oscuro disabled:opacity-50"
                >
                  {crear.isPending
                    ? tr('Creando…', 'Creating…')
                    : tr(`Crear todos (${elegidos.length})`, `Create all (${elegidos.length})`)}
                </button>
                {incompleto && (
                  <span className="text-xs text-apagado">
                    {tr(
                      'Falta la fecha de comercialización o el número o la fecha de algún C.O.',
                      'The commercialization date or some C.O. number or date is missing.',
                    )}
                  </span>
                )}
              </div>
            </>
          )}
          {error && <p className="text-alerta">{error}</p>}
          {resultado && (
            <div className="rounded-md border border-linea/80 p-3">
              <p className="font-medium text-verde-oscuro">{resultado.mensaje}</p>
              <ul className="mt-1 list-disc pl-5 text-xs">
                {resultado.recolecciones.flatMap(r =>
                  r.creados.map(c => (
                    <li key={`${r.recoleccion}-${c.origenId ?? c.analisisId}`}>
                      {r.recoleccion}: {c.numero !== null ? `C.O. ${c.numero}` : ''} {c.analisis ?? ''} ·{' '}
                      {numero(c.bigBags)} BB
                    </li>
                  )),
                )}
              </ul>
              <p className="mt-1 text-xs text-apagado">
                {tr(
                  'Descárgalos en "Paquete de una exportación" (abajo) o uno por uno en la lista.',
                  'Download them in "Export package" (below) or one by one in the list.',
                )}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
